import React, { useContext, useEffect, useState } from "react";
import { message, Button, Empty, Form, Input, Modal, PageHeader, Result, Space, Table, Checkbox } from "antd";
import { ColumnsType } from "antd/es/table";
import { DeleteFilled, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { AppContext } from "../state/State";

const { confirm } = Modal;

interface User {
    id: string;
    email: string;
    userName: string;
    organizationId: number;
    organization: string;
}

const Users: React.FC = () => {
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [users, setUsers] = useState<Array<User>>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const fetchUsers = async () => {
        setLoading(true);
        setHasError(false);

        try {
            const response = await fetch("/api/User/OrganizationUsers", {
                method: "GET",
                headers: {
                    Accept: "application/json"
                }
            });

            if (!response.ok) {
                setHasError(true);
                return;
            }

            setUsers(await response.json());
        } catch (error) {
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = async (response: Response) => {
        try {
            const data = await response.json();

            if (Array.isArray(data?.errors)) {
                return data.errors.join(" ");
            }

            if (typeof data?.title === "string") {
                return data.title;
            }
        } catch (error) {
            return "Request failed";
        }

        return "Request failed";
    };

    const showModal = () => {
        form.resetFields();
        form.setFieldsValue({ sendEmail: true });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        form.resetFields();
        setIsModalVisible(false);
    };

    const handleCreate = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            const response = await fetch("/api/User/OrganizationUsers", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    email: values.email,
                    password: values.password,
                    sendEmail: values.sendEmail || false
                })
            });

            if (!response.ok) {
                message.error(await getErrorMessage(response));
                return;
            }

            const result = await response.json();
            message.success(result?.emailSent ? "User created and email sent" : "User created");
            await fetchUsers();
            handleCancel();
        } catch (error) {
            if ((error as { errorFields?: Array<unknown> }).errorFields) {
                return;
            }

            message.error("Error creating user");
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (user: User) => {
        confirm({
            title: `Delete ${user.email}?`,
            icon: <ExclamationCircleOutlined />,
            content: "This action cannot be undone!",
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            onOk: async () => {
                try {
                    const response = await fetch(`/api/User/OrganizationUsers/${user.id}`, {
                        method: "DELETE",
                        headers: {
                            Accept: "application/json"
                        }
                    });

                    if (!response.ok) {
                        message.error(await getErrorMessage(response));
                        return;
                    }

                    message.success("User deleted");
                    await fetchUsers();
                } catch (error) {
                    message.error("Error deleting user");
                }
            }
        });
    };

    const columns: ColumnsType<User> = [
        {
            title: "Email",
            dataIndex: "email",
            sorter: (a, b) => a.email.toLocaleLowerCase().localeCompare(b.email.toLocaleLowerCase()),
            defaultSortOrder: "ascend"
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (text, record) => (
                <Space>
                    <Button
                        danger
                        type="primary"
                        icon={<DeleteFilled />}
                        disabled={record.id === context.userInfo?.id}
                        onClick={() => confirmDelete(record)}
                    ></Button>
                </Space>
            )
        }
    ];

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <>
            <PageHeader
                title="Users"
                style={{ paddingTop: "0" }}
                extra={[
                    <Button key="add" type="primary" onClick={showModal} icon={<PlusOutlined />}>
                        Add user
                    </Button>
                ]}
            />

            {hasError ? (
                <Result
                    status="error"
                    title="Unable to load users"
                    subTitle="Please check your connectivity and try again."
                    extra={<Button onClick={fetchUsers}>Retry</Button>}
                />
            ) : (
                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    pagination={{
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showSizeChanger: true,
                        defaultPageSize: 10
                    }}
                    dataSource={users}
                    locale={{
                        emptyText: !loading ? <Empty description="No users found" /> : null
                    }}
                ></Table>
            )}

            <Modal title="Add user" visible={isModalVisible} onOk={handleCreate} onCancel={handleCancel} confirmLoading={saving}>
                <Form form={form} layout="vertical" initialValues={{ sendEmail: true }}>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: "Please enter the user's email" },
                            { type: "email", message: "Please enter a valid email" }
                        ]}
                    >
                        <Input placeholder="Enter the user's email" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                            { required: true, message: "Please enter a password" },
                            { min: 8, message: "Password must be at least 8 characters" }
                        ]}
                    >
                        <Input.Password placeholder="Enter a password" />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="Password confirmation"
                        dependencies={["password"]}
                        rules={[
                            { required: true, message: "Please confirm the password" },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("password") === value) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(new Error("The password and confirmation password do not match."));
                                }
                            })
                        ]}
                    >
                        <Input.Password placeholder="Confirm the password" />
                    </Form.Item>
                    <Form.Item name="sendEmail" valuePropName="checked">
                        <Checkbox>Send an email to the new user</Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default Users;
